import React, { useContext, useEffect, useState } from 'react';
import { GlobalContext } from '../context/GlobalState';
import { MdClose } from 'react-icons/md';
import { SelectInstrument } from './Select/SelectInstrument';
import { SelectGenre } from './Select/SelectGenre';
import { getLocalAvatar } from '../utils/avatarUtils';
import { createOrUpdateCard } from '../utils/createOrUpdateCard';
import { uploadImage } from '../utils/uploadImage';
import { API_BASE_URL } from '../utils/apiBaseUrl';

export const MusicianCardForm = ({ setCreateMusicianCard }) => {
  const { userId, loadMusicians, showNotification } = useContext(GlobalContext);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [musicianData, setMusicianData] = useState({
    userId,
    firstName: '',
    lastName: '',
    instrument: [],
    genre: '',
    description: '',
    userApplied: [],
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    return () => {
      if (previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
      }
    };
  }, [previewImageUrl]);

  const validateForm = () => {
    const { firstName, lastName, instrument, genre, description } =
      musicianData;
    const errors = {};

    if (!firstName) {
      errors.firstName = 'First Name is required';
    }

    if (!lastName) {
      errors.lastName = 'Last Name is required';
    }

    if (instrument.length === 0) {
      errors.instrument = 'Instrument is required';
    }

    if (!genre) {
      errors.genre = 'Genre is required';
    }

    if (!description) {
      errors.description = 'Description is required';
    }

    setErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (fieldName, value) => {
    setMusicianData(prevData => ({
      ...prevData,
      [fieldName]: value,
    }));
  };

  const handleImageChange = event => {
    const file = event.target.files?.[0];

    if (!file) {
      setSelectedImage(null);
      if (previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
      }
      setPreviewImageUrl('');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setErrors(prevErrors => ({
        ...prevErrors,
        image: 'Please select a valid image file',
      }));
      setSelectedImage(null);
      if (previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
      }
      setPreviewImageUrl('');
      return;
    }

    if (previewImageUrl) {
      URL.revokeObjectURL(previewImageUrl);
    }

    setPreviewImageUrl(URL.createObjectURL(file));

    setErrors(prevErrors => ({
      ...prevErrors,
      image: '',
    }));
    setSelectedImage(file);
  };

  const handleSubmit = e => {
    e.preventDefault();

    if (validateForm()) {
      async function createCard() {
        setIsSubmitting(true);

        try {
          let image_url = getLocalAvatar(
            `${musicianData.firstName} ${musicianData.lastName}`,
          );
          let uploadedImageId = null;

          if (selectedImage) {
            const uploadedImage = await uploadImage(selectedImage);
            image_url = uploadedImage.image_url;
            uploadedImageId = uploadedImage.uploadedImageId;
          }

          const data = { ...musicianData, image_url, uploadedImageId };
          const response = await createOrUpdateCard(
            `${API_BASE_URL}/musicians`,
            data,
            'POST',
          );

          if (response?.ok) {
            await loadMusicians(true);
            showNotification('Musician card created successfully.', 'success');
            setTimeout(() => {
              setCreateMusicianCard(false);
            }, 1000);
          } else {
            showNotification('Could not create musician card.', 'error');
          }
        } catch (error) {
          showNotification(error.message || 'Image upload failed.', 'error');
        } finally {
          setIsSubmitting(false);
        }
      }
      createCard();
    }
  };

  return (
    <form className='form-mypage' onSubmit={handleSubmit}>
      <MdClose
        className='button-close'
        onClick={() => {
          setCreateMusicianCard(false);
        }}
      />
      <div className='form-item'>
        <label htmlFor='firstName'>First Name:</label>
        <input
          type='text'
          id='firstName'
          name='firstName'
          value={musicianData.firstName}
          onChange={e => handleInputChange('firstName', e.target.value)}
        />
        {errors.firstName && (
          <span className='message-error'>{errors.firstName}</span>
        )}
      </div>
      <div className='form-item'>
        <label htmlFor='lastName'>Last Name:</label>
        <input
          type='text'
          id='lastName'
          name='lastName'
          value={musicianData.lastName}
          onChange={e => handleInputChange('lastName', e.target.value)}
        />
        {errors.lastName && (
          <span className='message-error'>{errors.lastName}</span>
        )}
      </div>
      <div className='form-item'>
        <label htmlFor='instrument'>Instrument:</label>
        <SelectInstrument
          handleInputChange={handleInputChange}
          data={musicianData}
        />
        {errors.instrument && (
          <span className='message-error'>{errors.instrument}</span>
        )}
      </div>

      <div className='form-item'>
        <label htmlFor='genre'>Genre:</label>
        <SelectGenre
          handleInputChange={handleInputChange}
          data={musicianData}
        />
        {errors.genre && <span className='message-error'>{errors.genre}</span>}
      </div>
      <div className='form-item'>
        <label htmlFor='description'>Tell something:</label>
        <textarea
          id='description'
          name='description'
          value={musicianData.description}
          onChange={e => handleInputChange('description', e.target.value)}
        />
        {errors.description && (
          <span className='message-error'>{errors.description}</span>
        )}
      </div>
      <div className='form-item'>
        <label htmlFor='musicianImage'>Photo (optional):</label>
        <div className='form-image-preview-wrap'>
          <img
            className='form-image-preview'
            src={
              previewImageUrl ||
              getLocalAvatar(
                `${musicianData.firstName} ${musicianData.lastName}`,
              )
            }
            alt='Musician preview'
          />
        </div>
        <input
          type='file'
          id='musicianImage'
          name='musicianImage'
          accept='image/*'
          onChange={handleImageChange}
        />
        {errors.image && <span className='message-error'>{errors.image}</span>}
      </div>
      <button className='button-add' type='submit'>
        {isSubmitting ? 'Uploading...' : 'add'}
      </button>
    </form>
  );
};
